import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('karibu_token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: API_URL,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Add token to requests
    api.interceptors.request.use((config) => {
        const storedToken = localStorage.getItem('karibu_token');
        if (storedToken) {
            config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
    });

    // Auto-logout on session expired/terminated
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401 && error.response?.data?.detail?.includes('Session')) {
                localStorage.removeItem('karibu_token');
                setToken(null);
                setUser(null);
                window.location.href = '/auth';
            }
            return Promise.reject(error);
        }
    );

    const fetchUser = useCallback(async () => {
        const storedToken = localStorage.getItem('karibu_token');
        if (!storedToken) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('karibu_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (phone, pin) => {
        const response = await api.post('/auth/login', { phone, pin });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('karibu_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const register = async (phone, name, pin) => {
        const response = await api.post('/auth/register', { phone, name, pin });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('karibu_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const registerOperator = async (data) => {
        const response = await api.post('/auth/register/operator', data);
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('karibu_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch {}
        localStorage.removeItem('karibu_token');
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        registerOperator,
        logout,
        api,
        isAuthenticated: !!user,
        isTraveler: user?.role === 'traveler',
        isOperator: user?.role === 'operator',
        isAdmin: user?.role === 'admin',
        isTeamMember: user?.role === 'team_member',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
